import { el, ensureStyle } from '../dom';
import { t } from '../../strings';

// SVG progress ring: thick track + coloured arc, centred percentage in the
// display serif, small-caps caption below.

ensureStyle(
  'donut-ring-css',
  `
  /* Three-up row. Shared: Home leads with it, and the Stats screen's "The three
     rings" section shows the same three, at the same size, in the same order. */
  .ringrow { display:grid; grid-template-columns:repeat(3,1fr); gap:8px; }
  .ring { display:flex; flex-direction:column; align-items:center; gap:7px;
    color: var(--ink); }
  .ring svg { display:block; }
  /* Caption and fraction travel together, tighter than the gap to the ring:
     they are one label in two lines, not two separate notes. */
  .ring .cap-block { display:flex; flex-direction:column; align-items:center; gap:2px; }
  /* The percentage in the ring is a ratio; this is the count it was taken from.
     Tabular figures so the three of them line up across the row. */
  .ring .fig { font-family:var(--font-body); font-size:12.5px; line-height:1.3;
    color:var(--ink-soft); font-variant-numeric:tabular-nums; }
  /* SVG <text> paints with fill, not color — currentColor bridges the two so
     the percentage inherits the theme like everything else. */
  .ring .pct { font-family: var(--font-display); font-weight:600; font-size:21px; }
`,
);

export interface RingOpts {
  percent: number;
  caption: string;
  color: string;
  size?: number;
  /**
   * Spelled-out reading of the ring — "Sounds: 67% of the 12 you've met are
   * solid". A percentage and a one-word caption are not self-explanatory, so
   * this rides along for screen readers and as the hover title.
   */
  label?: string;
  /**
   * The count the percentage was taken from — "70 / 140" — set under the
   * caption. A ratio with no numbers behind it is a number you can't check.
   */
  detail?: string;
}

export function donutRing({
  percent,
  caption,
  color,
  size = 84,
  label,
  detail,
}: RingOpts): HTMLElement {
  const stroke = 9;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const p = Math.max(0, Math.min(100, percent));
  const dash = (p / 100) * c;
  const cx = size / 2;

  const svg = `
    <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
      <circle cx="${cx}" cy="${cx}" r="${r}" fill="none"
        stroke="var(--track)" stroke-width="${stroke}" />
      <circle cx="${cx}" cy="${cx}" r="${r}" fill="none"
        stroke="${color}" stroke-width="${stroke}" stroke-linecap="round"
        stroke-dasharray="${dash} ${c - dash}"
        transform="rotate(-90 ${cx} ${cx})" />
      <text x="50%" y="50%" text-anchor="middle" dominant-baseline="central"
        fill="currentColor" class="pct">${t('common.percent', { percent: p })}</text>
    </svg>`;

  return el(
    'div.ring',
    label ? { title: label, role: 'img', 'aria-label': label } : {},
    el('div', { html: svg }),
    el(
      'div.cap-block',
      {},
      el('div.cap.label', {}, caption),
      detail ? el('div.fig', {}, detail) : null,
    ),
  );
}
